import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { getCountryCodeOptions } from "@shared/utils";
import { cn } from "@shared/utils";

interface ContactStepProps {
  name: string;
  cedula: string;
  email: string;
  address: string;
  countryCode: string;
  phone: string;
  onNameChange: (v: string) => void;
  onCedulaChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  onAddressChange: (v: string) => void;
  onCountryCodeChange: (v: string) => void;
  onPhoneChange: (v: string) => void;
  /** Errores emitidos desde el padre al intentar continuar */
  fieldErrors?: Record<string, string>;
}

// ── Validators ──────────────────────────────────────────────────────────────

const ONLY_LETTERS = /^[^\d!@#$%^&*()_+=[\]{};':"\\|,.<>/?`~]*$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateName(v: string): string {
  if (!v.trim()) return "El nombre completo es obligatorio";
  if (!ONLY_LETTERS.test(v)) return "Solo se permiten letras y espacios";
  if (v.trim().split(/\s+/).length < 2) return "Ingresa nombre y apellido";
  return "";
}

function validateEmail(v: string): string {
  if (!v.trim()) return "El correo es requerido";
  if (!EMAIL_RE.test(v)) return "Ingresa un correo válido";
  return "";
}

/** Checkout step 1 — contact details (2-column layout). */
export function ContactStep({
  name,
  cedula,
  email,
  address,
  countryCode,
  phone,
  onNameChange,
  onCedulaChange,
  onEmailChange,
  onAddressChange,
  onCountryCodeChange,
  onPhoneChange,
  fieldErrors = {},
}: ContactStepProps) {
  const countryOptions = getCountryCodeOptions();

  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");

  // Errores del padre tienen prioridad sobre los locales
  const effectiveNameError = fieldErrors.name || nameError;
  const effectiveEmailError = fieldErrors.email || emailError;
  const effectiveCedulaError = fieldErrors.cedula ?? "";
  const effectivePhoneError = fieldErrors.phone ?? "";

  // Cédula: only digits, max 13 (RUC length)
  const handleCedulaChange = (v: string) => {
    const digits = v.replace(/\D/g, "").slice(0, 13);
    onCedulaChange(digits);
  };

  // Teléfono: only digits, strip leading 0, max 9 digits (el +593 va por separado)
  const handlePhoneChange = (v: string) => {
    const digits = v.replace(/\D/g, "").replace(/^0+/, "").slice(0, 9);
    onPhoneChange(digits);
  };

  // Nombre: filter invalid chars, validate on blur
  const handleNameChange = (v: string) => {
    // Blacklist de símbolos y dígitos — permite cualquier letra Unicode (ñ, tildes, etc.)
    const filtered = v.replace(/[\d!@#$%^&*()_+=[\]{};':"\\|,.<>/?`~]/g, "");
    onNameChange(filtered);
    if (nameError) setNameError(validateName(filtered));
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="font-semibold text-black" style={{ fontSize: 20 }}>
        Detalles del contacto
      </span>

      {/* 2-column grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        <Field
          label="Nombre completo"
          value={name}
          onChange={handleNameChange}
          onBlur={() => setNameError(validateName(name))}
          placeholder="Nombre y apellido"
          error={effectiveNameError}
        />
        <Field
          label="Cédula / RUC"
          value={cedula}
          onChange={handleCedulaChange}
          placeholder="Ingresa tu cédula o RUC"
          inputMode="numeric"
          maxLength={13}
          error={effectiveCedulaError}
        />
        <Field
          label="Correo electrónico"
          value={email}
          onChange={(v) => {
            onEmailChange(v);
            if (emailError) setEmailError(validateEmail(v));
          }}
          onBlur={() => setEmailError(validateEmail(email))}
          placeholder="correo@ejemplo.com"
          type="email"
          error={effectiveEmailError}
        />
        <Field
          label="Dirección"
          value={address}
          onChange={onAddressChange}
          placeholder="Ingresa tu dirección"
        />
      </div>

      {/* Phone row */}
      <div className="flex gap-4">
        {/* Country code */}
        <div className="flex flex-col gap-1.5">
          <span className="font-semibold text-black" style={{ fontSize: 14 }}>
            País
          </span>
          <div className="relative">
            <select
              value={countryCode}
              onChange={(e) => onCountryCodeChange(e.target.value)}
              className="h-[50px] w-[120px] appearance-none rounded-lg border border-neutral-20 bg-white px-3 pr-8 text-black outline-none"
              style={{ fontSize: 14 }}
            >
              {countryOptions.map((opt) => (
                <option key={opt.code} value={opt.callingCode}>
                  {opt.callingCode} ({opt.code})
                </option>
              ))}
            </select>
            <ChevronDown
              size={24}
              className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-black"
            />
          </div>
        </div>

        {/* Phone number */}
        <div className="flex flex-1 flex-col gap-1.5">
          <span className="font-semibold text-black" style={{ fontSize: 14 }}>
            Número de teléfono
          </span>
          <input
            type="tel"
            inputMode="numeric"
            maxLength={9}
            value={phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder="98XXXXXXX"
            className={cn(
              "h-[50px] w-full rounded-lg border bg-white px-4 text-black outline-none placeholder:text-neutral-70",
              effectivePhoneError ? "border-red-500" : "border-neutral-20",
            )}
            style={{ fontSize: 14 }}
          />
          {effectivePhoneError && (
            <span className="text-red-500" style={{ fontSize: 12 }}>
              {effectivePhoneError}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/** Reusable inline input field for checkout form. */
function Field({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  type = "text",
  inputMode,
  maxLength,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  placeholder: string;
  type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  maxLength?: number;
  error?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="font-semibold text-black" style={{ fontSize: 14 }}>
        {label}
      </span>
      <input
        type={type}
        inputMode={inputMode}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        className={cn(
          "h-[50px] rounded-lg border bg-white px-4 text-black outline-none placeholder:text-neutral-70",
          error ? "border-red-500" : "border-neutral-20",
        )}
        style={{ fontSize: 14 }}
      />
      {error && (
        <span className="text-red-500" style={{ fontSize: 12 }}>
          {error}
        </span>
      )}
    </div>
  );
}
